// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import GridContainer from "@store/MobiusOverrides/GridContainer/GridContainer";
import cancelDraftFieldValues from "@store/Framework/Store/Greif/Components/ContactUsTopicsForm/Handlers/CancelDraftFieldValues";
import saveDraftFieldValues from "@store/Framework/Store/Greif/Components/ContactUsTopicsForm/Handlers/SaveDraftFieldValues";
import setDraftFieldValues from "@store/Framework/Store/Greif/Components/ContactUsTopicsForm/Handlers/SetDraftFieldValues";
import { useStepperContextStep } from "@store/Framework/Components/StepperContext";
import { ContactUsTopicsFormFieldValues } from "@store/Framework/Store/Greif/Components/ContactUsTopicsForm/ContactUsTopicsFormState";
import FormStep, { FormStepPresentationProps, FormStepProps } from "@store/Library/Components/FormStepper/FormStep";
import TextAreaFormField, {
    TextAreaFormFieldPresentationProps,
} from "@store/Library/Components/FormField/TextAreaFormField";
import FileUploadFormField, {
    FileUploadFormFieldPresentationProps,
} from "@store/Library/Components/FormField/FileUploadFormField";
import PlantFormField, {
    plantFormFieldLabelKey,
    PlantFormFieldPresentationProps,
} from "@store/Library/Components/FormField/PlantFormField";
import ReferenceProductIdFormField, {
    ReferenceProductIdFormFieldPresentationProps,
} from "@store/Library/Components/FormField/ReferenceProductIdFormField";
import FormFieldUploadedFile from "@store/Library/Components/FormField/FormFieldUploadedFile";
import { formStepProps } from "@store/Themes/common/props/formStep";
import ReferenceProduct from "@store/Library/Components/ReferenceProduct";
import React, { FC, useRef, useState } from "react";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { css } from "styled-components";

const stepFieldValueKeys: Array<keyof ContactUsTopicsFormFieldValues> = [
    "attachments",
    "location",
    "message",
    "referenceProductId",
];

export interface ContactUsTopicsFormDetailsStepProps extends Partial<Omit<FormStepProps, "contextStepNumber">> {
    isPlantRequired?: boolean;
}

export interface ContactUsTopicsFormDetailsStepStyles {
    attachmentsInfoValue?: TypographyPresentationProps;
    fileUploadFormField?: FileUploadFormFieldPresentationProps;
    formGridContainer?: GridContainerProps;
    formGridItem?: GridItemProps;
    formStep?: FormStepPresentationProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    plantFormField?: PlantFormFieldPresentationProps;
    messageFormField?: TextAreaFormFieldPresentationProps;
    referenceProductIdFormField?: ReferenceProductIdFormFieldPresentationProps;
}

export const contactUsTopicsFormDetailsStepStyles: ContactUsTopicsFormDetailsStepStyles = {
    formGridContainer: formStepProps.formGridContainer,
    formGridItem: {
        ...formStepProps.formGridItem,
        width: [12, 12, 6, 6, 6],
    },
    infoGridContainer: formStepProps.infoGridContainer,
    infoGridItem: formStepProps.infoGridItem,
    infoLabel: formStepProps.infoLabel,
    infoValue: formStepProps.infoValue,
    attachmentsInfoValue: mergeProps<TypographyPresentationProps>(formStepProps.infoValue, {
        forwardAs: "div",
        css: css`
            width: 100%;
        `,
    }),
};

const styles = contactUsTopicsFormDetailsStepStyles;

const ContactUsTopicsFormDetailsStep: FC<ContactUsTopicsFormDetailsStepProps> = ({
    isPlantRequired,
    ...formStepProps
}) => {
    const dispatch = useAppDispatch();
    const [showErrors, setShowErrors] = useState(false);
    const { isComplete, stepNumber } = useStepperContextStep();
    const draftFieldValues = useAppSelector(state => state.greif.components.contactUsTopicsForm.formDraftFieldValues);
    const fieldValues = useAppSelector(state => state.greif.components.contactUsTopicsForm.formFieldValues);
    const referenceProductIsChecked = useRef(true);

    if (!stepNumber) {
        return null;
    }

    const attachmentsLabel = translate("Attachments");
    const plantLabel = translate(`${plantFormFieldLabelKey}`);
    const messageLabel = translate("Message");
    const messageErrorMessage = siteMessage("ContactUsTopicsForm_MessageRequired").toString();
    const savedAttachments = fieldValues.attachments.value;
    const referenceProductLabel = translate("Reference Product");
    const selectedLocationLabel = fieldValues.location.displayValue || fieldValues.location.value || "—";

    return (
        <FormStep
            {...styles.formStep}
            contextStepNumber={stepNumber}
            onCancel={() => dispatch(cancelDraftFieldValues(stepFieldValueKeys))}
            onSubmit={event => {
                if (!showErrors) {
                    setShowErrors(true);
                }

                if (!stepFieldValueKeys.every(x => draftFieldValues[x].isValid !== false)) {
                    event.preventDefault();
                    return;
                }

                dispatch(saveDraftFieldValues(stepFieldValueKeys));
            }}
            title={translate("Details")}
            {...formStepProps}
        >
            {isComplete ? (
                <GridContainer {...styles.infoGridContainer}>
                    {fieldValues.referenceProductId.value && (
                        <GridItem {...styles.infoGridItem} width={12}>
                            <Typography {...styles.infoLabel}>{referenceProductLabel}</Typography>
                            <ReferenceProduct productId={fieldValues.referenceProductId.value} />
                        </GridItem>
                    )}
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{plantLabel}</Typography>
                        <Typography {...styles.infoValue}>{selectedLocationLabel}</Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{attachmentsLabel}</Typography>
                        <Typography {...styles.attachmentsInfoValue}>
                            {savedAttachments.length
                                ? savedAttachments.map((attachment, index) => (
                                      <FormFieldUploadedFile
                                          file={attachment}
                                          key={`${attachment.fileName}-${index}`}
                                      />
                                  ))
                                : "—"}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem} width={12}>
                        <Typography {...styles.infoLabel}>{messageLabel}</Typography>
                        <Typography {...styles.infoValue}>{fieldValues.message.value || "—"}</Typography>
                    </GridItem>
                </GridContainer>
            ) : (
                <GridContainer {...styles.formGridContainer}>
                    <GridItem {...styles.formGridItem} width={12}>
                        <ReferenceProductIdFormField
                            {...styles.referenceProductIdFormField}
                            defaultChecked={referenceProductIsChecked.current}
                            defaultValue={draftFieldValues.referenceProductId.value}
                            onChange={(event, checked) => {
                                referenceProductIsChecked.current = checked;
                            }}
                            onUpdate={referenceProductId => dispatch(setDraftFieldValues({ referenceProductId }))}
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <PlantFormField
                            {...styles.plantFormField}
                            defaultValue={draftFieldValues.location.value}
                            label={plantLabel}
                            onUpdate={location => dispatch(setDraftFieldValues({ location }))}
                            required={isPlantRequired}
                            showError={showErrors}
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <FileUploadFormField
                            {...styles.fileUploadFormField}
                            accept=".pdf,.zip"
                            defaultValue={draftFieldValues.attachments.value}
                            label={attachmentsLabel}
                            multiple
                            onUpdate={attachments => dispatch(setDraftFieldValues({ attachments }))}
                            showError={showErrors}
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem} width={12}>
                        <TextAreaFormField
                            {...styles.messageFormField}
                            defaultValue={draftFieldValues.message.value}
                            label={messageLabel}
                            minLength={10}
                            minLengthErrorMessage={messageErrorMessage}
                            onUpdate={message => dispatch(setDraftFieldValues({ message }))}
                            placeholder={siteMessage("ContactUsTopicsForm_MessagePlaceholder").toString()}
                            required
                            requiredErrorMessage={messageErrorMessage}
                            showError={showErrors}
                        />
                    </GridItem>
                </GridContainer>
            )}
        </FormStep>
    );
};

export default ContactUsTopicsFormDetailsStep;
